<template>
  <div class="progressPage" id="progressPage" ref="progressPage">
    <TopBack :title="$t('lang.progress.title')"/>

    <div class="page-body">
      <!-- 概览卡 -->
      <section class="card overview-card">
        <h2 class="ov-title">{{ $t(overview.programKey) }}</h2>
        <p class="ov-sub">{{ $t('lang.progress.dayOf', {cur: overview.dayCur, total: overview.dayTotal}) }}</p>

        <div class="ov-progress-row">
          <span class="ov-progress-label">{{ $t('lang.progress.progress') }}</span>
          <span class="ov-progress-pct">{{ $t('lang.progress.pctCompleted', {pct: overview.pct}) }}</span>
        </div>
        <div class="progress-track">
          <div class="progress-fill" :style="{width: overview.pct + '%'}"></div>
        </div>

        <div class="stats-box">
          <div class="stat">
            <span class="stat-label">{{ $t('lang.progress.lockedDeposit') }}</span>
            <span class="stat-value">{{ $t('lang.progress.amountBc', {amount: separateThousandInteger(overview.lockedDeposit)}) }}</span>
            <span class="stat-link">{{ $t('lang.progress.unlocksDay', {day: overview.unlockDay}) }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">{{ $t('lang.progress.expectedYield') }}</span>
            <span class="stat-value brand">{{ $t('lang.progress.amountBc', {amount: separateThousandInteger(overview.yield)}) }}</span>
            <span class="stat-note">{{ $t('lang.progress.atSettlement') }}</span>
          </div>
        </div>

        <p class="pid">{{ overview.pid }}</p>
      </section>

      <!-- 每日安排标题 -->
      <section class="schedule-header">
        <h3 class="schedule-title">{{ $t('lang.progress.dailySchedule') }}</h3>
        <p class="schedule-sub">{{ $t('lang.progress.scheduleSub') }}</p>
      </section>

      <div class="task-list">
        <!-- 当前进行中 Day 2 -->
        <div class="card day-active">
          <div class="day-row">
            <div class="badge-circle">
              <svg viewBox="0 0 24 24" class="ic-play"><path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z"/></svg>
            </div>
            <div class="day-info">
              <div class="day-top">
                <div class="day-name-wrap">
                  <span class="day-name">{{ $t('lang.progress.dayName', {n: activeDay.n}) }}</span>
                  <span class="pill pill-today">{{ $t('lang.progress.today') }}</span>
                </div>
                <span class="day-pct">{{ $t('lang.progress.pct', {pct: activeDay.pct}) }}</span>
              </div>
              <p class="day-sub">{{ $t('lang.progress.tasksCompleted', {done: activeDay.done, total: activeDay.total}) }}</p>
            </div>
          </div>
          <div class="progress-track progress-track--tight">
            <div class="progress-fill" :style="{width: activeDay.pct + '%'}"></div>
          </div>
          <button class="btn-continue" type="button" @click="onContinue">
            <span>{{ $t('lang.progress.continue') }}</span>
            <svg viewBox="0 0 24 24" class="ic-arrow" fill="none" stroke="currentColor" stroke-width="2"><path d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>

        <!-- 锁定 Day 3-7 -->
        <div class="card day-locked" v-for="d in lockedDays" :key="d.name">
          <div class="day-row">
            <div class="badge-square">
              <svg v-if="!d.settlement" viewBox="0 0 24 24" class="ic-lock" fill="none" stroke="currentColor" stroke-width="1.75">
                <rect height="10" rx="2" width="14" x="5" y="11"/><path d="M8 11V7a4 4 0 1 1 8 0v4"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" class="ic-pig" fill="none" stroke="currentColor" stroke-width="1.75">
                <path d="M19 11V9a2 2 0 0 0-2-2h-3l-2-2H8a2 2 0 0 0-2 2v2a5 5 0 0 0-1 4.58V18a2 2 0 0 0 2 2h1v-1a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v1h1a2 2 0 0 0 2-2v-2.42A5 5 0 0 0 19 11z" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="15.5" cy="11.5" fill="currentColor" r="0.75"/><path d="M11 9h2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="locked-text">
              <span class="locked-name">{{ d.settlement ? $t('lang.progress.settlementName', {n: d.n}) : $t('lang.progress.dayName', {n: d.n}) }}</span>
              <span class="locked-sub">{{ $t('lang.progress.unlocksAfter', {n: d.after}) }}</span>
            </div>
            <span class="pill pill-locked">{{ $t('lang.progress.locked') }}</span>
          </div>
        </div>

        <!-- 已完成 Day 1 -->
        <div class="card day-completed">
          <div class="day-row">
            <div class="badge-circle">
              <svg viewBox="0 0 20 20" class="ic-check"><path clip-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" fill-rule="evenodd"/></svg>
            </div>
            <div class="locked-text">
              <span class="completed-name">{{ $t('lang.progress.dayName', {n: completedDay.n}) }}</span>
              <span class="completed-sub">{{ $t('lang.progress.tasksCompleted', {done: completedDay.done, total: completedDay.total}) }}</span>
            </div>
            <span class="pill pill-completed">{{ $t('lang.progress.completed') }}</span>
          </div>
        </div>
      </div>

      <!-- 底部提示 -->
      <aside class="notice">
        <div class="notice-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line stroke-linecap="round" x1="12" x2="12" y1="16" y2="12"/><circle cx="12" cy="8" fill="currentColor" r="1"/>
          </svg>
        </div>
        <p class="notice-text">{{ $t('lang.progress.notice') }}</p>
      </aside>
    </div>
  </div>
</template>

<script>
import {separateThousandInteger} from '@/utils'
import TopBack from '@/components/TopBack/index'

export default {
  name: 'TrainingProgress',
  components: {TopBack},
  data () {
    return {
      // 静态占位数据：文案走 i18n（programKey + progress.* 模板），数值为基准屏 P2 (b0b3cc96)。
      // 后端 Training Progress 接口尚未提供，service/service2 不在本次改动范围；接口就绪后改为 mounted 请求并填充同名结构。
      overview: {
        programKey: 'lang.training.programTitles.p7d',
        dayCur: 2,
        dayTotal: 7,
        pct: 28,
        lockedDeposit: 20000,
        unlockDay: 7,
        yield: 1400,
        pid: '#193783463(PID)'
      },
      activeDay: {n: 2, pct: 60, done: 6, total: 10},
      lockedDays: [
        {n: 3, after: 2},
        {n: 4, after: 3},
        {n: 5, after: 4},
        {n: 6, after: 5},
        {n: 7, after: 6, settlement: true}
      ],
      completedDay: {n: 1, done: 10, total: 10}
    }
  },
  methods: {
    separateThousandInteger,
    onContinue () {
      // TODO: 进入当日任务（训练答题）流程
    }
  }
}
</script>

<style lang='less' scoped>
.progressPage {
  position: relative;
  min-height: 100dvh;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  background: var(--stitch-page-bg);
}

.page-body {
  padding-top: calc(60vw / 3.6); /* TopBack 占位 */
  padding-bottom: calc(24vw / 3.6);
}

/* ---- 通用卡片 ---- */
.card {
  background: var(--stitch-surface);
  border: calc(1vw / 3.6) solid var(--stitch-card-border);
  border-radius: var(--stitch-radius-2xl);
  box-shadow: 0 calc(1vw / 3.6) calc(2vw / 3.6) rgba(0, 0, 0, .04);
}

/* ---- 概览卡 ---- */
.overview-card { margin: calc(12vw / 3.6) calc(16vw / 3.6) 0; padding: calc(16vw / 3.6); }
.ov-title { font-size: calc(17vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); line-height: 1.375; }
.ov-sub { font-size: calc(12vw / 3.6); color: var(--stitch-text-secondary); font-weight: 500; margin-top: calc(2vw / 3.6); }
.ov-progress-row { margin-top: calc(14vw / 3.6); display: flex; align-items: center; justify-content: space-between; font-size: calc(12vw / 3.6); font-weight: 600; }
.ov-progress-label { color: var(--stitch-text-primary); font-weight: 700; }
.ov-progress-pct { color: var(--stitch-brand); font-weight: 700; }

.progress-track { width: 100%; background: var(--stitch-track); height: calc(6vw / 3.6); border-radius: var(--stitch-radius-full); margin-top: calc(6vw / 3.6); overflow: hidden; }
.progress-track--tight { margin-top: calc(-2vw / 3.6); }
.progress-fill { background: var(--stitch-brand); height: 100%; border-radius: var(--stitch-radius-full); }

.stats-box { margin-top: calc(14vw / 3.6); background: var(--stitch-stats-bg); border-radius: var(--stitch-radius-xl); padding: calc(12vw / 3.6); display: flex; justify-content: space-between; align-items: flex-start; }
.stat { display: flex; flex-direction: column; }
.stat-label { font-size: calc(11vw / 3.6); color: var(--stitch-text-secondary); font-weight: 500; }
.stat-value { font-size: calc(14vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); margin-top: calc(2vw / 3.6); line-height: 1.25; }
.stat-value.brand { color: var(--stitch-brand); }
.stat-link { font-size: calc(11vw / 3.6); font-weight: 500; color: var(--stitch-brand); margin-top: calc(2vw / 3.6); }
.stat-note { font-size: calc(11vw / 3.6); color: var(--stitch-text-secondary); margin-top: calc(2vw / 3.6); }
.pid { font-size: calc(10vw / 3.6); color: var(--stitch-text-weak); font-family: monospace; margin-top: calc(10vw / 3.6); }

/* ---- 每日安排标题 ---- */
.schedule-header { padding: 0 calc(16vw / 3.6); margin: calc(20vw / 3.6) 0 calc(10vw / 3.6); }
.schedule-title { font-size: calc(14vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); letter-spacing: -0.01em; }
.schedule-sub { font-size: calc(12vw / 3.6); color: var(--stitch-text-secondary); margin-top: calc(2vw / 3.6); }

/* ---- 任务列表 ---- */
.task-list { padding: 0 calc(16vw / 3.6); display: flex; flex-direction: column; gap: calc(10vw / 3.6); }

.day-active { padding: calc(16vw / 3.6); display: flex; flex-direction: column; gap: calc(12vw / 3.6); }
.day-row { display: flex; align-items: center; gap: calc(12vw / 3.6); }
.badge-circle { width: calc(40vw / 3.6); height: calc(40vw / 3.6); border-radius: var(--stitch-radius-full); background: var(--stitch-icon-badge); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--stitch-brand); }
.ic-play { width: calc(20vw / 3.6); height: calc(20vw / 3.6); fill: currentColor; margin-left: calc(2vw / 3.6); }
.ic-check { width: calc(20vw / 3.6); height: calc(20vw / 3.6); fill: currentColor; }
.day-info { flex: 1; min-width: 0; }
.day-top { display: flex; align-items: center; justify-content: space-between; }
.day-name-wrap { display: flex; align-items: center; gap: calc(6vw / 3.6); }
.day-name { font-size: calc(14vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); line-height: 1; }
.day-pct { font-size: calc(12vw / 3.6); font-weight: 700; color: var(--stitch-brand); }
.day-sub { font-size: calc(12vw / 3.6); color: var(--stitch-text-secondary); margin-top: calc(4vw / 3.6); }

.pill { font-size: calc(10vw / 3.6); font-weight: 600; padding: calc(2vw / 3.6) calc(8vw / 3.6); border-radius: var(--stitch-radius-full); line-height: 1.4; }
.pill-today { background: var(--stitch-brand); color: var(--stitch-surface); }

.btn-continue { width: 100%; height: calc(40vw / 3.6); background: var(--stitch-brand); color: var(--stitch-surface); font-weight: 600; font-size: calc(12vw / 3.6); border: none; border-radius: var(--stitch-radius-lg); display: flex; align-items: center; justify-content: center; gap: calc(6vw / 3.6); box-shadow: 0 calc(1vw / 3.6) calc(2vw / 3.6) rgba(0, 81, 223, .18); margin-top: calc(2vw / 3.6); cursor: pointer; }
.btn-continue:active { background: var(--stitch-brand-active); }
.ic-arrow { width: calc(16vw / 3.6); height: calc(16vw / 3.6); }

/* 锁定卡 */
.day-locked { background: var(--stitch-page-bg); border-color: rgba(226, 232, 240, .5); padding: calc(12vw / 3.6); box-shadow: 0 calc(1vw / 3.6) calc(2vw / 3.6) rgba(0, 0, 0, .02); }
.day-locked .day-row { justify-content: space-between; }
.badge-square { width: calc(40vw / 3.6); height: calc(40vw / 3.6); border-radius: var(--stitch-radius-xl); background: var(--stitch-locked-icon-bg); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--stitch-text-weak); }
.ic-lock, .ic-pig { width: calc(20vw / 3.6); height: calc(20vw / 3.6); }
.locked-text { display: flex; flex-direction: column; }
.locked-name { font-size: calc(12vw / 3.6); font-weight: 700; color: #1E293B; line-height: 1.25; }
.locked-sub { font-size: calc(11vw / 3.6); color: var(--stitch-text-secondary); margin-top: calc(2vw / 3.6); }
.pill-locked { background: var(--stitch-track); color: var(--stitch-text-mid); }

/* 完成卡 */
.day-completed { padding: calc(12vw / 3.6); }
.day-completed .day-row { justify-content: space-between; }
.completed-name { font-size: calc(12vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); line-height: 1.25; }
.completed-sub { font-size: calc(11vw / 3.6); color: var(--stitch-text-secondary); margin-top: calc(2vw / 3.6); }
.pill-completed { background: var(--stitch-icon-badge); color: var(--stitch-brand); }

/* 底部提示 */
.notice { margin: calc(12vw / 3.6) calc(16vw / 3.6) 0; background: var(--stitch-stats-bg); border-radius: var(--stitch-radius-2xl); padding: calc(12vw / 3.6); display: flex; align-items: flex-start; gap: calc(10vw / 3.6); border: calc(1vw / 3.6) solid rgba(239, 246, 255, .5); }
.notice-icon { color: var(--stitch-brand); flex-shrink: 0; margin-top: calc(2vw / 3.6); }
.notice-icon svg { width: calc(16vw / 3.6); height: calc(16vw / 3.6); display: block; }
.notice-text { font-size: calc(11vw / 3.6); color: var(--stitch-text-mid); line-height: 1.4; }
</style>



// WEBPACK FOOTER //
// src/pages/TrainingProgress/index.vue